import callRpc from '~/utils/call-rpc';
import { BufferUpload, Upload } from '../entities/Upload';
import { VideoDetails, VideoMetadata } from '../values/VideoMetadata';
import { ImageDimensions } from '../values/ImageDimensions';
import { UploadStatus } from '../constants';

type MediaResponse = {
  success: boolean;
  upload_id: string;
  location: string;
  type: 'photo' | 'video';
  message?: string;
};

type ImageResponse = MediaResponse & {
  type: 'photo';
  width: number | null;
  height: number | null;
};

type VideoResponse = MediaResponse & {
  type: 'video';
  location: string;
  transcodeVideo: boolean;
  details: VideoDetails;
};

export const BufferUploadsApi = {
  async create(
    upload: Upload,
    { forceTranscode = false }: { forceTranscode?: boolean } = {}
  ): Promise<BufferUpload> {
    const response: ImageResponse | VideoResponse = await callRpc(
      'composerApiProxy',
      {
        url: `/i/uploads/upload_media.json`,
        args: {
          key: upload.key,
          serviceForceTranscodeVideo: forceTranscode,
        },
      }
    );

    if (!response.success) {
      throw new Error(response.message || 'Unknown error calling upload_media');
    }

    const url = response.location;
    const uploadId = response.upload_id;
    const isPhotoResponse = response.type === 'photo';
    const isVideResponse = response.type === 'video';
    const isPhotoUpload = Upload.isImage(upload) || Upload.isGif(upload);
    const isVideoUpload = Upload.isVideo(upload);

    if (!url || !uploadId) {
      throw new Error(
        'Upload failed: response is missing url Upload failed: response is missing url or uploadId'
      );
    }

    if (isPhotoResponse && isPhotoUpload) {
      const { width, height } = response;
      const dimensions = ImageDimensions.new({ width, height });

      return {
        ...upload,
        status: UploadStatus.Completed,
        url,
        uploadId,
        dimensions,
      };
    }

    if (isVideResponse && isVideoUpload) {
      const { transcodeVideo, details } = response;

      const videoMetadata = VideoMetadata.newFromDetails(
        details,
        transcodeVideo
      );

      return {
        ...upload,
        status: UploadStatus.Completed,
        url,
        uploadId,
        videoMetadata,
      };
    }

    throw new Error(
      `Upload failed: backend type was ${response.type} and upload type was ${upload.mediaType}`
    );
  },
};
